'use client';

import { useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const SavedItems = ({ savedItems, onMoveToCart, onRemoveSaved }) => {
  const [expandedItems, setExpandedItems] = useState(false);

  if (savedItems?.length === 0) {
    return null;
  }

  const displayItems = expandedItems ? savedItems : savedItems?.slice(0, 3);

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 mt-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl lg:text-2xl font-semibold text-foreground">
          Saved for Later ({savedItems?.length})
        </h2>
        {savedItems?.length > 3 && (
          <button
            onClick={() => setExpandedItems(!expandedItems)}
            className="text-sm caption text-primary hover:text-primary/80 transition-smooth focus:outline-none focus:ring-2 focus:ring-ring rounded px-2 py-1"
          >
            {expandedItems ? 'Show Less' : `Show All (${savedItems?.length})`}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {displayItems?.map((item) => (
          <div key={item?.id} className="bg-muted rounded-lg p-4 hover:shadow-md transition-smooth">
            <Link href={`/product-details?id=${item?.id}`} className="block aspect-square overflow-hidden rounded-md bg-background mb-3 focus:outline-none focus:ring-2 focus:ring-ring">
              <AppImage
                src={item?.image}
                alt={item?.alt}
                className="w-full h-full object-cover hover:scale-105 transition-smooth"
              />
            </Link>

            <Link href={`/product-details?id=${item?.id}`} className="focus:outline-none focus:ring-2 focus:ring-ring rounded">
              <h3 className="text-sm md:text-base font-semibold text-foreground mb-1 line-clamp-2 hover:text-primary transition-smooth">
                {item?.name}
              </h3>
            </Link>

            <p className="text-xs caption text-muted-foreground mb-2">
              Part: <span className="data-text">{item?.partNumber}</span>
            </p>

            <div className="flex items-center justify-between mb-3">
              <span className="text-base md:text-lg font-bold text-primary data-text">
                ${item?.price?.toLocaleString('en-US')}
              </span>
              <div className="flex items-center gap-1">
                <Icon name="MapPinIcon" size={14} className="text-muted-foreground" />
                <span className="text-xs caption text-muted-foreground">{item?.location}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => onMoveToCart(item?.id)}
                className="flex-1 h-10 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-md transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
              >
                Move to Cart
              </button>
              <button
                onClick={() => onRemoveSaved(item?.id)}
                className="w-10 h-10 flex items-center justify-center bg-background hover:bg-background/80 text-error rounded-md transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Remove saved item"
              >
                <Icon name="TrashIcon" size={18} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

SavedItems.propTypes = {
  savedItems: PropTypes?.arrayOf(
    PropTypes?.shape({
      id: PropTypes?.string?.isRequired,
      name: PropTypes?.string?.isRequired,
      partNumber: PropTypes?.string?.isRequired,
      image: PropTypes?.string?.isRequired,
      alt: PropTypes?.string?.isRequired,
      price: PropTypes?.number?.isRequired,
      location: PropTypes?.string?.isRequired
    })
  )?.isRequired,
  onMoveToCart: PropTypes?.func?.isRequired,
  onRemoveSaved: PropTypes?.func?.isRequired
};

export default SavedItems;